import { useQuery } from '@tanstack/react-query';
import { errorMessage, fetchInvestStrategiesCatalog, type InvestStrategyCatalogItem } from './api';

export type StrategyParams = Record<string, unknown>;

export function parseInvestStrategyParams(value: string | StrategyParams): StrategyParams {
  if (typeof value !== 'string') return value;
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as StrategyParams : {};
  } catch {
    return {};
  }
}

export function useInvestStrategyCatalog() {
  return useQuery({ queryKey: ['invest-strategies-catalog'], queryFn: fetchInvestStrategiesCatalog });
}

export function catalogForParams(catalog: InvestStrategyCatalogItem[] | undefined, params: StrategyParams) {
  const type = typeof params.type === 'string' ? params.type : '';
  return catalog?.find((item) => item.type === type) ?? null;
}

function InfoMark() {
  return <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 10.5v6M12 7.5h.01"/></svg>;
}

function StrategyParameterValue({ name, value }: { name: string; value: unknown }) {
  if (name === 'weights' && value && typeof value === 'object' && !Array.isArray(value)) {
    return <b className="invest-strategy-guide-weight-chips">{Object.entries(value as Record<string, unknown>).map(([symbol, weight]) => <span key={symbol}>{symbol} <i>{String(weight)}%</i></span>)}</b>;
  }
  if (Array.isArray(value)) return <b>{value.join(', ')}</b>;
  if (value && typeof value === 'object') return <b>{JSON.stringify(value)}</b>;
  return <b>{String(value)}</b>;
}

export function StrategyInfoButton({ label, expanded, onClick }: { label: string; expanded: boolean; onClick: () => void }) {
  return <button type="button" className="invest-strategy-info-button" aria-label={label} aria-expanded={expanded} onClick={onClick}><InfoMark/></button>;
}

export function InvestStrategyGuidePanel({ params, catalog, compact = false }: {
  params: string | StrategyParams;
  catalog: InvestStrategyCatalogItem[] | undefined;
  compact?: boolean;
}) {
  const parsed = parseInvestStrategyParams(params);
  const guide = catalogForParams(catalog, parsed);
  if (!guide) return <aside className={`invest-strategy-guide${compact ? ' is-compact' : ''}`}><strong>Tipo non riconosciuto</strong><p>Questa strategia usa parametri legacy o personalizzati. Il JSON resta disponibile per il controllo tecnico.</p></aside>;
  return <aside className={`invest-strategy-guide${compact ? ' is-compact' : ''}`} aria-label={`Come funziona ${guide.nome}`}>
    <header><div><p className="eyebrow">Catalogo strategie</p><h4>{guide.nome}</h4></div><code>{guide.type}</code></header>
    <p className="invest-strategy-philosophy">{guide.filosofia}</p>
    <dl className="invest-strategy-rules"><div><dt>Considera</dt><dd>{guide.cosaConsidera}</dd></div><div><dt>Compra</dt><dd>{guide.quandoCompra}</dd></div><div><dt>Vende</dt><dd>{guide.quandoVende}</dd></div></dl>
    <div className="invest-strategy-parameter-list"><strong>Parametri usati</strong>{Object.entries(parsed).filter(([key]) => key !== 'type').length > 0 ? <ul>{Object.entries(parsed).filter(([key]) => key !== 'type').map(([key, value]) => <li key={key}><span><code>{key}</code><button type="button" aria-label={`Spiegazione parametro ${key}`} title={guide.parametri[key] ?? 'Parametro specifico della candidata'}><InfoMark/></button></span><StrategyParameterValue name={key} value={value}/></li>)}</ul> : <p>Nessun parametro aggiuntivo.</p>}</div>
  </aside>;
}

export function InvestCatalogState({ error, retry }: { error: unknown; retry: () => void }) {
  return <div className="invest-catalog-error" role="alert"><p>{errorMessage(error)}</p><button type="button" className="btn-secondary" onClick={retry}>Riprova</button></div>;
}
